import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import FeedItem from "@/components/FeedItem";
import PreloadStack from "@/components/PreloadStack";
import AdItem from "@/components/AdItem";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Coins } from "@phosphor-icons/react";
import { useDataSaver } from "@/context/DataSaverContext";
import { useAuth } from "@/context/AuthContext";
import { feedPosition, feedKey } from "@/lib/feedPosition";
import { ScrollHint } from "@/components/ScrollHint";
import { SpotlightCard } from "@/components/SpotlightCard";
import { getNetworkLocation, cachedLocation, spotParams } from "@/lib/nearby";

const SPOTLIGHT_EVERY = 5;

const RESTART = "__restart__";

export default function Feed() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enabled: dataSaver } = useDataSaver();
  const { user } = useAuth();
  const deepLinkId = searchParams.get("video_id") || searchParams.get("post_id") || null;
  const [scope, setScope] = useState(() => {
    return sessionStorage.getItem("tain-feed-scope") === "following" ? "following" : "for-you";
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockInfo, setLockInfo] = useState({ remaining: 0, cost: 1, admin_whatsapp: "" });
  const [deepLinkError, setDeepLinkError] = useState(null);
  const containerRef = useRef(null);
  const seenRef = useRef(new Set());
  const scrolledToDeepLinkRef = useRef(false);

  const pageSize = dataSaver ? 6 : 12;

  const [pointsBalance, setPointsBalance] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const spotlightAskedRef = useRef(false);
  const postCount = items.filter((it) => it.type === "post").length;

  // Load promoted businesses once the first post is in; refine with the network location.
  useEffect(() => {
    if (spotlightAskedRef.current || postCount < 1) return;
    spotlightAskedRef.current = true;
    const fetchSpot = (loc) => api.get("/market/spotlight", { params: spotParams(loc) })
      .then(({ data }) => data?.businesses || []);
    (async () => {
      // Show what we can straight away, then refine once the network location is known.
      const first = await fetchSpot(cachedLocation());
      setSpotlight(first);
      const loc = await getNetworkLocation();
      if (loc.places.length || loc.lat != null) setSpotlight(await fetchSpot(loc));
    })().catch(() => setSpotlight((s) => s || []));
  }, [postCount]);

  const displayItems = useMemo(() => {
    if (!spotlight) return items;
    const out = [];
    let n = 0;
    items.forEach((it) => {
      out.push(it);
      if (it.type === "post" && ++n % SPOTLIGHT_EVERY === 0) {
        const slot = n / SPOTLIGHT_EVERY;
        out.push({ type: "spotlight", data: { business: spotlight.length ? spotlight[(slot - 1) % spotlight.length] : null, slot } });
      }
    });
    // Short feeds (fewer than 5 posts) still get one card at the end.
    if (n > 0 && n < SPOTLIGHT_EVERY && !cursor) {
      out.push({ type: "spotlight", data: { business: spotlight[0] || null, slot: 1 } });
    }
    return out;
  }, [items, spotlight, cursor]);

  const loadViewing = useCallback(async () => {
    try {
      const { data } = await api.get("/me/viewing");
      setViewing(data);
    } catch { /* ignore */ }
    // Also refresh points balance for the top-right badge.
    try {
      const { data } = await api.get("/wallet/summary");
      setPointsBalance(data?.points ?? null);
    } catch { /* ignore */ }
  }, []);

  const genRef = useRef(0);

  const fetchFollowing = useCallback(async (s, before) => {
    const { data } = await api.get("/feed", { params: { scope: s, limit: pageSize, ...(before ? { before } : {}) } });
    return { items: data.items || [], next: data.next_cursor || null };
  }, [pageSize]);

  // Oldest-first cycle: resume after the last post watched on this device; at the
  // end of the feed, start again from the very first upload.
  const fetchUnseen = useCallback(async (s, startAfter) => {
    if (startAfter === RESTART) genRef.current += 1;
    let after = startAfter === RESTART ? null : (startAfter || feedPosition.get());
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { data } = await api.get("/feed", {
        params: { scope: s, limit: pageSize, order: "asc", ...(after ? { after } : {}) },
      });
      const gen = genRef.current;
      const items = (data.items || []).map((it) => (
        it.type === "post" && !it.data.is_pinned_tutorial ? { ...it, gen, pos: feedKey(it.data) } : it));
      if (items.some((it) => it.type === "post") || !after) {
        return { items, next: data.next_cursor || (items.length ? RESTART : null) };
      }
      // Stored position was the last post — the cycle wraps to the oldest upload.
      genRef.current += 1;
      after = null;
    }
    return { items: [], next: null };
  }, [pageSize]);

  const load = useCallback(async (nextScope, opts = {}) => {
    const s = nextScope || scope;
    const isMore = !!opts.before;
    if (isMore) setLoadingMore(true); else setLoading(true);
    try {
      const { items: newItems, next } = s === "following"
        ? await fetchFollowing(s, opts.before)
        : await fetchUnseen(s, opts.before);
      // If a deep-link video_id is present and this is the initial load (not
      // pagination), fetch that post and pin it to the top — de-duping so it
      // doesn't appear twice if the natural feed happens to include it.
      let merged = newItems;
      if (!isMore && deepLinkId) {
        try {
          const { data: pinned } = await api.get(`/posts/${deepLinkId}/detail`);
          if (pinned && pinned.post_id) {
            const pinnedItem = { type: "post", data: pinned };
            merged = [
              pinnedItem,
              ...newItems.filter((it) => it.type !== "post" || it.data?.post_id !== pinned.post_id),
            ];
            setDeepLinkError(null);
          }
        } catch (e) {
          const detail = e?.response?.data?.detail;
          setDeepLinkError(
            e?.response?.status === 404
              ? "That video no longer exists"
              : (typeof detail === "string" ? detail : "Couldn't load the shared video"),
          );
        }
      }
      setItems((prev) => (isMore ? [...prev, ...merged] : merged));
      setCursor(next);
    } catch (e) {
      toast.error("Failed to load feed");
    } finally {
      if (isMore) setLoadingMore(false); else setLoading(false);
    }
  }, [scope, deepLinkId, fetchFollowing, fetchUnseen]);

  useEffect(() => {
    load(scope);
    loadViewing();
    sessionStorage.setItem("tain-feed-scope", scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Deep-link: after the first successful load with a pinned video, scroll to
  // it and strip the query param so the URL becomes clean (?video_id=… gets
  // removed once the video is on screen — a page reload still lands on the
  // pinned clip because we only strip AFTER navigation completes).
  useEffect(() => {
    if (!deepLinkId || loading || scrolledToDeepLinkRef.current) return;
    if (!containerRef.current) return;
    const target = containerRef.current.querySelector(`[data-key="${CSS.escape(deepLinkId)}"]`);
    if (target) {
      target.scrollIntoView({ behavior: "auto", block: "start" });
      scrolledToDeepLinkRef.current = true;
      // Strip the query param (keep the pinned item in state).
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkId, loading, items, setSearchParams]);

  // If the deep-link fetch failed, surface a toast once.
  useEffect(() => {
    if (deepLinkError) {
      toast.error(deepLinkError);
      setDeepLinkError(null);
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkError, setSearchParams]);

  // Poll the feed while any post in it is still processing. Matches Profile.jsx
  // behavior so a video that finishes transcoding shows up in the current feed
  // without a manual refresh. Only these processing states trigger polling —
  // any other status (ready / failed / done / etc.) leaves the interval cleared.
  useEffect(() => {
    const hasProcessing = items.some((it) => {
      if (it.type !== "post") return false;
      const p = it.data || {};
      return (
        p.processing_status === "processing" ||
        ["init", "uploading", "queued", "processing"].includes(p.processing_session_status)
      );
    });
    if (!hasProcessing) return;
    const t = setInterval(() => { load(scope); }, 3000);
    return () => clearInterval(t);
  }, [items, scope, load]);

  // Track the currently-visible feed item so we can pre-warm the next-3 videos'
  // HLS master + first fragment(s). Combined with hls.js `startLevel:0` this means
  // swiping to the next video paints frame-1 in well under a second on stable networks.
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            // Update active index so PreloadStack can pre-warm the next 2.
            const idx = parseInt(entry.target.dataset.index || "0", 10);
            setActiveIndex(idx);
            // Belt-and-braces: force every OTHER video in the feed to
            // pause + mute so scrolled-past clips never leak audio into
            // the current one. The per-item IntersectionObserver already
            // pauses, but stalled buffers or Safari MSE quirks can let
            // audio linger a beat — this hard-enforces the invariant.
            try {
              const activeEl = entry.target;
              containerRef.current?.querySelectorAll("video").forEach((v) => {
                if (activeEl.contains(v)) return;
                try {
                  v.muted = true;
                  if (!v.paused) v.pause();
                } catch { /* ignore */ }
              });
            } catch { /* ignore */ }
            const key = entry.target.dataset.key;
            const type = entry.target.dataset.itemType;
            if (entry.target.dataset.pos) feedPosition.markSeen(entry.target.dataset.pos, Number(entry.target.dataset.gen || 0));
            if (!key || seenRef.current.has(key)) return;
            seenRef.current.add(key);
            if (type === "post") {
              try {
                // Record the view for the creator's counters. We deliberately
                // IGNORE `data.locked` from the legacy watch-to-unlock system —
                // the admin-configurable points economy (below) is the single
                // source of truth for locking the feed now.
                const { data } = await api.post(`/posts/${key}/view`);
                if (data?.success) loadViewing();
              } catch { /* ignore */ }
              // Admin-configured feed-view point deduction (server-side).
              // Only shows the lock dialog when the user is genuinely OUT of
              // points AND past the admin-set free-videos grace period.
              try {
                const { data: pt } = await api.post(`/points/feed-view`);
                if (pt.deducted > 0) {
                  toast(pt.message || `${pt.deducted} pt deducted`, { duration: 2000 });
                }
                if (pt.insufficient) {
                  setLockInfo({ remaining: pt.remaining || 0, cost: pt.cost || 1,
                                 admin_whatsapp: pt.admin_whatsapp || lockInfo.admin_whatsapp || "" });
                  setLockOpen(true);
                }
              } catch { /* ignore */ }
            } else if (type === "ad") {
              api.post(`/ads/${key}/view`).catch(() => {});
            }
          }
        });
      },
      { threshold: [0.6] }
    );
    const elements = containerRef.current.querySelectorAll("[data-key]");
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [displayItems, loadViewing]);

  // Infinite scroll — fetch more when the last item enters view
  useEffect(() => {
    if (!cursor || loadingMore || items.length === 0 || !containerRef.current) return;
    const els = containerRef.current.querySelectorAll("[data-key]");
    if (els.length === 0) return;
    const last = els[els.length - 1];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          load(scope, { before: cursor });
          io.disconnect();
        }
      });
    }, { rootMargin: "300px" });
    io.observe(last);
    return () => io.disconnect();
  }, [cursor, loadingMore, items, displayItems, scope, load]);

  // Global credit-lock listener. When ANY FeedItem (video, photo, text)
  // receives a 402 from `/qualify`, it fires this event so we lock the
  // whole feed — the modal is undismissable, blocks continuous scroll
  // and points the user to admin WhatsApp / Buy Credits per §17.
  useEffect(() => {
    const onLock = (e) => {
      const d = (e && e.detail) || {};
      setLockInfo({
        remaining: Number(d.remaining || 0),
        cost: Number(d.cost || 0),
        admin_whatsapp: d.admin_whatsapp || "",
      });
      setLockOpen(true);
    };
    window.addEventListener("tain:credit-lock", onLock);
    return () => window.removeEventListener("tain:credit-lock", onLock);
  }, []);

  const switchScope = (s) => {
    if (s === scope) return;
    seenRef.current = new Set();
    setItems([]);
    setCursor(null);
    setScope(s);
  };

  const unlockNext = async () => {
    // Kept for backwards compatibility with any old buttons; sends the user
    // to Buy Points where they can top up.
    navigate("/buy-points");
  };

  return (
    <div className="relative w-full flex justify-center bg-[#0A0A0A]">
      {/* Floating top tabs */}
      <div
        data-testid="feed-tabs"
        className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-6 px-4 py-2"
      >
        <FeedTab label="Following" active={scope === "following"} onClick={() => switchScope("following")} testId="feed-tab-following" />
        <span className="text-white/25 text-xs">|</span>
        <FeedTab label="For You" active={scope === "for-you"} onClick={() => switchScope("for-you")} testId="feed-tab-for-you" />
      </div>

      <div
        ref={containerRef}
        data-testid="feed-container"
        className="feed-container w-full max-w-md h-[calc(100dvh-7rem)] md:h-screen overflow-y-scroll"
      >
        {loading ? (
          <div className="h-[calc(100dvh-7rem)] md:h-screen flex items-center justify-center text-white/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="h-[calc(100dvh-7rem)] md:h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="font-heading text-2xl font-bold">
              {scope === "following" ? "Nothing from creators you follow yet" : "No content yet"}
            </p>
            <p className="text-white/60 max-w-md">
              {scope === "following"
                ? "Follow creators from the For-You feed or Discover to fill this up."
                : "Be the first to post. Pay the upload Credits and start earning from every like, save, share and gift."}
            </p>
          </div>
        ) : (
          displayItems.map((it, idx) => {
            const key = it.type === "post" ? it.data.post_id
              : it.type === "spotlight" ? `spotlight-${it.data.slot}` : it.data.ad_id;
            return (
              <div
                key={`${it.type}-${key}-${idx}`}
                data-key={key}
                data-item-type={it.type}
                data-pos={it.pos}
                data-gen={it.gen}
                data-index={idx}
                className="feed-item h-[calc(100dvh-7rem)] md:h-screen w-full"
              >
                {it.type === "post" ? <FeedItem post={it.data} index={idx} onUpdate={() => load(scope)} />
                  : it.type === "spotlight" ? <SpotlightCard biz={it.data.business} />
                  : <AdItem ad={it.data} />}
              </div>
            );
          })
        )}
      </div>

      {!loading && items.length > 0 && <ScrollHint isGuest={!user} />}

      {/* Silently pre-warm the next 2 videos (first ~2 seconds each) so a swipe
          to the next video paints its first frame instantly. Requests are
          cancelled if the user scrolls past them quickly. */}
      <PreloadStack items={displayItems} activeIndex={activeIndex} count={3} />

      {/* Out-of-points lock — undismissable until user tops up */}
      <Dialog open={lockOpen}>
        <DialogContent
          data-testid="feed-lock-modal"
          hideClose
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="bg-[#121212] border-white/10 text-white max-w-sm text-center"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center justify-center gap-2">
              <Lock size={26} weight="fill" className="text-yellow-400" />
              You&apos;re out of Credits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/80 leading-relaxed">
              You&apos;ve used all your viewing Credits. To continue watching,
              Buy Credits from Tainapp on Whatsapp.
            </p>
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-xs uppercase tracking-widest text-yellow-300 font-bold">Credits balance</p>
              <p className="font-heading text-3xl font-black text-white" data-testid="feed-lock-balance">
                {lockInfo.remaining} cr
              </p>
            </div>
            {lockInfo.admin_whatsapp && (
              <Button
                data-testid="feed-lock-whatsapp-btn"
                onClick={() => window.open(lockInfo.admin_whatsapp, "_blank", "noopener")}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-black font-heading font-black gap-2"
              >
                Message admin on WhatsApp
              </Button>
            )}
            <Button
              data-testid="feed-lock-buy-btn"
              onClick={() => navigate("/buy-points")}
              className="w-full h-12 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-heading font-black gap-2"
            >
              <Coins size={20} weight="fill" /> Buy Credits
            </Button>
            <p className="text-[11px] text-white/40 flex items-center justify-center gap-1">
              <Lock size={11} weight="bold" /> Viewing is paused until you top up
            </p>
            <p className="text-[11px] text-white/40">
              By buying you agree to our{" "}
              <a href="/terms" data-testid="feed-lock-terms-link" className="underline hover:text-white/70">
                Terms of Service
              </a>.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeedTab({ label, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`relative whitespace-nowrap font-heading font-black text-base tracking-tight transition-all ${
        active ? "text-white text-lg drop-shadow-[0_2px_10px_rgba(255,255,255,0.35)]" : "text-white/75 hover:text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]"
      }`}
    >
      {label}
      {active && (
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-full bg-white" />
      )}
    </button>
  );
}
